import { useId, useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "../../tooltip/Tooltip";

/**
 * StatusOverflow component for handling status text overflow with ellipsis and hover tooltip.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.label - Text or node to display inside status item.
 * @param {string} [props.targetId] - Target ID for anchor selection of Tooltip.
 * @param {string} [props.tooltipId] - Custom tooltip ID fallback.
 * @param {Object} [props.style] - Inline style overrides for text container.
 * @param {string} [props.className] - Additional class name for text container.
 */
export const StatusOverflow = ({
    label,
    targetId,
    tooltipId,
    style = {},
    className = "",
}) => {
    const textRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const generatedId = useId().replace(/:/g, "");
    const resolvedTargetId = targetId || tooltipId || `status-overflow-${generatedId}`;

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || !label) return;

        const checkOverflow = () => {
            const isOverflow = el.scrollWidth > el.clientWidth;
            setShowTooltip(prev => (prev !== isOverflow ? isOverflow : prev));
        };

        checkOverflow();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(checkOverflow);
            observer.observe(el);
            return () => observer.disconnect();
        }
    }, [label]);

    return (
        <>
            <span
                ref={textRef}
                id={targetId ? undefined : resolvedTargetId}
                className={`status-overflow-text ${className}`}
                style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    maxWidth: "100%",
                    minWidth: 0,
                    ...style,
                }}
            >
                {label}
            </span>

            {showTooltip && (
                <Tooltip
                    label={typeof label === "string" ? label : String(label || "")}
                    targetId={targetId || resolvedTargetId}
                />
            )}
        </>
    );
};
